import { Stack } from '@mui/joy';
import { forwardRef, useImperativeHandle } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IAuthUser } from 'auth/types';
import { Form } from 'core/components';
import { LabeledValue } from 'core/elements';
import { useFormContext } from 'core/states';
import { useAddBomPartsToQuoteContext } from 'quotes/components/AddBomPartsToQuote';
import {
  CustomerQuoteUpsertForm,
  SET_AS_DEFAULT_FOR_CONTACT_HIDDEN_FIELDS,
} from 'quotes/components/CustomerQuoteUpsert';
import {
  ICreateCustomerQuoteFromQuoteForm,
  ICreateVendorQuoteFromQuoteForm,
} from 'quotes/components/QuotePartAddToQuote/Steps/SelectItemsToCopy/types';
import { VendorQuoteUpsertForm } from 'quotes/components/VendorQuoteUpsert';
import { QuoteEntityType, QuoteType } from 'quotes/enums';
import { Direction, Level } from 'theme/enums';

import { ICreateCustomerQuoteForm, ICreateVendorQuoteForm } from './types';

interface IProps {
  addToQuoteType: QuoteType;
  user: IAuthUser;
  itemsToCopyFields: JSX.Element;
  isAlternateVersionEnabled: boolean;
  defaultValues: ICreateCustomerQuoteForm | ICreateVendorQuoteForm;
  entityType?: QuoteEntityType;
}

export const SelectItemsToCopyFormFields = forwardRef(function SelectItemsToCopyFormFields(
  {
    addToQuoteType = QuoteType.CUSTOMER,
    user,
    itemsToCopyFields,
    isAlternateVersionEnabled,
    defaultValues,
    entityType = QuoteEntityType.QUOTE,
  }: IProps,
  ref
) {
  const { t } = useTranslation();

  const { trigger, getValues } = useFormContext();
  const { selectedQuote, selectedContact } = useAddBomPartsToQuoteContext();

  useImperativeHandle(
    ref,
    () => ({
      triggerFormSubmit: () => {
        return new Promise((resolve, reject) => {
          trigger()
            .then((isValid) => {
              if (isValid) {
                resolve(getValues());
              } else {
                reject();
              }
            })
            .catch(reject);
        });
      },
    }),
    [getValues, trigger]
  );

  const onClose = (
    reset: UseFormReset<ICreateCustomerQuoteFromQuoteForm | ICreateVendorQuoteFromQuoteForm>
  ) => {
    reset(defaultValues);
  };

  const contactLabel =
    QuoteType.VENDOR === addToQuoteType
      ? t('labelVendorQuoteContact', { entityType })
      : t('labelCustomerQuoteCustomer');

  const quoteNumberLabel =
    QuoteType.VENDOR === addToQuoteType
      ? t('labelVendorQuoteNumberShort', { entityType })
      : t('labelCustomerQuoteNumberShort', { entityType });

  return (
    <>
      {selectedQuote?.id && (
        <Stack gap={2}>
          <LabeledValue
            label={contactLabel}
            value={selectedContact.name}
            options={{
              labelLevel: Level.BODY_SM,
              valueLevel: Level.TITLE_MD,
              wrapperProps: {
                spacing: 0.5,
                direction: Direction.ROW,
                alignItems: 'center',
              },
            }}
          />

          <LabeledValue
            label={quoteNumberLabel}
            value={selectedQuote.id.toString()}
            options={{
              labelLevel: Level.BODY_SM,
              valueLevel: Level.TITLE_MD,
              wrapperProps: {
                spacing: 0.5,
                direction: Direction.ROW,
                alignItems: 'center',
              },
            }}
          />

          <Form isOpen withoutButtons inline withoutNotification>
            {itemsToCopyFields}
          </Form>
        </Stack>
      )}

      {!selectedQuote?.id && QuoteType.CUSTOMER === addToQuoteType && (
        <CustomerQuoteUpsertForm<ICreateCustomerQuoteFromQuoteForm>
          isOpen
          isAlternateVersionEnabled={isAlternateVersionEnabled}
          onClose={onClose}
          user={user}
          defaultValues={defaultValues}
          entityType={entityType}
          hiddenFields={SET_AS_DEFAULT_FOR_CONTACT_HIDDEN_FIELDS}
          extraFields={itemsToCopyFields}
          withoutForm
          withoutNotification
        />
      )}

      {!selectedQuote?.id && QuoteType.VENDOR === addToQuoteType && (
        <VendorQuoteUpsertForm
          isOpen
          onClose={onClose}
          entityType={entityType}
          extraFields={itemsToCopyFields}
          withoutForm
          withoutNotification
        />
      )}
    </>
  );
});
